import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, List, Grid, Link, ListItem, Typography, ListItemText } from '@material-ui/core';

import { useStyles, listTextStyles, customCardStyles } from './SkuDetails.styled';
import { SkusActions } from 'state/modules/skus';
import ItemStatus from 'components/ItemStatus';
import { withRouter } from 'react-router-dom';
import { Card } from 'components/UI';
import i18n from 'i18n-js';
import { capitalize } from 'utils';
import { useConfirm } from 'hooks';
import Can from 'components/Can';
import { UpdateSkuModal } from 'components/Modals';

const SkuDetails = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();
  const [openUpdateSkuModal, setOpenUpdateSkuModal] = useState(false);
  const { sku, fetchedDeleteSku, fetchedUpdateSku } = useSelector((state) => state.skus);
  const editable = !sku?.frozen_time;

  useEffect(() => {
    if (id) {
      dispatch(SkusActions.fetchSkuById(id));
    }
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (fetchedDeleteSku) {
      dispatch(SkusActions.fetchSkuById(id));
    }
  }, [id, dispatch, fetchedDeleteSku]);

  useEffect(() => {
    if (fetchedUpdateSku) {
      dispatch(SkusActions.fetchSkuById(id));
    }
  }, [id, dispatch, fetchedUpdateSku]);

  /**
   * Parse sku data
   * @param {object} sku Object with sku data
   */
  const getSkuData = (sku = {}) => {
    const skuInfo = [
      { label: `${i18n.t(`skus.type`)}:`, value: `${sku?.pricing_type?.external_name || '-'}` },
      { label: `${i18n.t(`skus.name`)}:`, value: sku?.name || '-' },
      {
        label: `${i18n.t(`skus.units`)}:`,
        value: capitalize(sku?.units?.total?.toLowerCase()) || '-',
      },
      {
        label: `${i18n.t(`skus.invoice_description`)}:`,
        value: sku?.description || '-',
        classes: 'fullWidth',
      },
      { label: `${i18n.t(`skus.note`)}:`, value: sku?.notes || '-', classes: 'fullWidth' },
    ];

    return skuInfo;
  };

  /**
   * Open change user status modal
   */
  const handleChangeStatus = () => {
    dispatch(
      SkusActions.fetchUpdateSku(id, {
        status: 'ACTIVE',
      })
    );
  };

  /**
   * Delete SKU
   * @param {object} row Data of the row selected
   */
  const handleDeleteSku = () => {
    dispatch(SkusActions.fetchDeleteSku(id));
  };

  const [toggleDeleteSku, DeleteSkuConfirm] = useConfirm({
    title: i18n.t('skus.delete_sku'),
    message: i18n.t('skus.delete_sku_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.archive'),
    onConfirm: handleDeleteSku,
  });

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: sku?.status === 'ACTIVE' ? i18n.t('skus.archive') : i18n.t('skus.activate'),
      onClick: sku?.status === 'ACTIVE' ? toggleDeleteSku : handleChangeStatus,
    },
  ];
  /**
   * Open update sku modal
   */
  const handleUpdateSkuModal = () => {
    setOpenUpdateSkuModal((prevState) => !prevState);
  };

  return (
    <Card
      menuItems={editable ? items : undefined}
      menuPermissions={{
        action: 'update',
        subject: 'invoice_item_type',
        type: 'can',
      }}
      title={i18n.t('skus.basic_info')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
    >
      <Box className={classes.cardContentPadding}>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item xs={8}>
            <Typography variant="h3">
              <Box className={classes.name}>ID# {id}</Box>
            </Typography>
          </Grid>
          <Grid item xs={4} className={`${classes.textRight}`}>
            <ItemStatus
              i18nKey="userStatus"
              status={sku?.status || (sku?.active ? 'ACTIVE' : 'DELETED')}
            />
          </Grid>
        </Grid>
        <Can I="update" a="invoice_item_type">
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item xs={12} className={`${classes.textRight}`}>
              <Typography>
                <Link
                  href="#"
                  onClick={handleUpdateSkuModal}
                  data-cy="update-info"
                  className={classes.skuLink}
                >
                  {i18n.t('skus.update_information')}
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Can>

        <List className={classes.skuList}>
          {getSkuData(sku).map((val, index) => (
            <ListItem key={index} className={classes.skuListItem}>
              <ListItemText
                classes={{
                  root: val.classes || listClasses.root,
                  primary: listClasses.primary,
                  secondary: listClasses.secondary,
                }}
                primary={val.label}
                secondary={
                  <Box
                    display="flex"
                    component="span"
                    flexDirection="column"
                    className={!val.classes ? classes.textRight : ''}
                  >
                    <Typography variant="body2" component="span" color="textSecondary">
                      {val.value}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        <DeleteSkuConfirm />

        {openUpdateSkuModal && (
          <UpdateSkuModal id={id} open={openUpdateSkuModal} handleClick={handleUpdateSkuModal} />
        )}
      </Box>
    </Card>
  );
};

export default withRouter(SkuDetails);
